import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
interface HeroSphereProps {
  className?: string;
  size?: number;
}
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}
export function HeroSphere({
  className = '',
  size = 550
}: HeroSphereProps) {
  const isMobile = useIsMobile();
  const colorOrange = '#E8781A';
  const colorPurple = '#a855f7';
  const logoSize = size * 0.45;
  // Mobile: simplified static sphere — no SVG filters, no infinite animations
  if (isMobile) {
    return <div className={`relative flex items-center justify-center overflow-hidden ${className}`} style={{
      width: size,
      height: size
    }} data-id="element-92">
        {/* Simple glow */}
        <div className="absolute inset-0 rounded-full" style={{
        boxShadow: `0 0 60px rgba(232,120,26,0.2), 0 0 120px rgba(232,120,26,0.1)`
      }} data-id="element-93" />
        {/* Sphere base */}
        <div className="absolute inset-4 rounded-full" style={{
        background: `radial-gradient(circle at 30% 30%, rgba(232,120,26,0.3) 0%, rgba(10,10,10,0.9) 60%, #000 100%)`,
        boxShadow: `inset -15px -15px 40px rgba(168,85,247,0.15), inset 15px 15px 40px rgba(232,120,26,0.15)`
      }} data-id="element-94" />
        {/* Logo — static */}
        <div className="absolute z-10 flex items-center justify-center" style={{
        width: logoSize,
        height: logoSize
      }} data-id="element-95">
          <img src="https://cdn.magicpatterns.com/uploads/4ht1x7WAqNUtZXYZ5Ksjq7/Zeichenflache_4.png" alt="fooble media Logo" className="w-full h-full object-contain" style={{
          filter: 'drop-shadow(0 0 15px rgba(232,120,26,0.25))'
        }} data-id="element-96" />
        </div>
        {/* Ring contained within bounds */}
        <div className="absolute rounded-full border border-[#E8781A]/20" style={{
        width: '95%',
        height: '95%'
      }} data-id="element-97" />
      </div>;
  }
  // Desktop: full animated sphere
  return <motion.div className={`relative flex items-center justify-center ${className}`} style={{
    width: size,
    height: size
  }} initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 1.5,
    delay: 0.5,
    ease: 'easeOut'
  }} data-id="element-98">
      {/* Outer Glow (Pulsing) */}
      <motion.div className="absolute inset-0 rounded-full" style={{
      boxShadow: `0 0 60px rgba(232,120,26,0.3), 0 0 120px rgba(232,120,26,0.15), 0 0 200px rgba(168,85,247,0.1)`
    }} animate={{
      opacity: [0.6, 1, 0.6],
      scale: [0.95, 1.05, 0.95]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-99" />

      {/* Solid Sphere Base */}
      <div className="absolute inset-4 rounded-full" style={{
      background: `radial-gradient(circle at 30% 30%, rgba(232,120,26,0.4) 0%, rgba(10,10,10,0.9) 60%, #000 100%)`,
      boxShadow: `inset -20px -20px 60px rgba(168,85,247,0.2), inset 20px 20px 60px rgba(232,120,26,0.2)`
    }} data-id="element-100" />

      {/* Inner Core (Bright Spot) */}
      <motion.div className="absolute rounded-full blur-xl" style={{
      width: size * 0.4,
      height: size * 0.4,
      background: `radial-gradient(circle, ${colorOrange} 0%, transparent 70%)`,
      opacity: 0.8
    }} animate={{
      scale: [0.9, 1.1, 0.9],
      opacity: [0.6, 0.9, 0.6]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-101" />

      {/* Logo Integration */}
      <motion.div className="absolute z-10 flex items-center justify-center" style={{
      width: logoSize,
      height: logoSize
    }} animate={{
      scale: [1, 1.04, 1],
      opacity: [0.85, 1, 0.85]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-102">
        <img src="https://cdn.magicpatterns.com/uploads/4ht1x7WAqNUtZXYZ5Ksjq7/Zeichenflache_4.png" alt="fooble media Logo" className="w-full h-full object-contain" style={{
        filter: 'drop-shadow(0 0 20px rgba(232,120,26,0.3)) drop-shadow(0 0 60px rgba(232,120,26,0.15))'
      }} data-id="element-103" />
      </motion.div>

      {/* Concentric Rings */}
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-104">
        <motion.div className="absolute rounded-full border border-[#E8781A]/30" style={{
        width: '110%',
        height: '110%'
      }} animate={{
        rotate: 360
      }} transition={{
        duration: 40,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-105" />
        <motion.div className="absolute rounded-full border border-[#a855f7]/20 border-dashed" style={{
        width: '125%',
        height: '125%'
      }} animate={{
        rotate: -360
      }} transition={{
        duration: 50,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-106" />
        <motion.div className="absolute rounded-full border-2 border-[#E8781A]/10" style={{
        width: '140%',
        height: '140%'
      }} animate={{
        rotate: 360
      }} transition={{
        duration: 60,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-107" />
      </div>

      {/* Electric Arcs (SVG) */}
      <div className="absolute inset-0 overflow-hidden rounded-full" data-id="element-108">
        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none" data-id="element-109">
          <defs data-id="element-110">
            <linearGradient id="arcGrad1" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-111">
              <stop offset="0%" stopColor={colorOrange} stopOpacity="0" data-id="element-112" />
              <stop offset="50%" stopColor={colorOrange} stopOpacity="1" data-id="element-113" />
              <stop offset="100%" stopColor={colorPurple} stopOpacity="0" data-id="element-114" />
            </linearGradient>
            <linearGradient id="arcGrad2" x1="100%" y1="0%" x2="0%" y2="100%" data-id="element-115">
              <stop offset="0%" stopColor={colorPurple} stopOpacity="0" data-id="element-116" />
              <stop offset="50%" stopColor={colorPurple} stopOpacity="1" data-id="element-117" />
              <stop offset="100%" stopColor={colorOrange} stopOpacity="0" data-id="element-118" />
            </linearGradient>
            <filter id="glow" data-id="element-119">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-120" />
              <feMerge data-id="element-121">
                <feMergeNode in="coloredBlur" data-id="element-122" />
                <feMergeNode in="SourceGraphic" data-id="element-123" />
              </feMerge>
            </filter>
          </defs>
          <path d="M 10,50 C 30,10 70,10 90,50" fill="none" stroke="url(#arcGrad1)" strokeWidth="0.5" filter="url(#glow)" className="arc-animate-1" data-id="element-124" />
          <path d="M 15,30 C 40,90 60,90 85,30" fill="none" stroke="url(#arcGrad2)" strokeWidth="0.5" filter="url(#glow)" className="arc-animate-2" data-id="element-125" />
          <path d="M 50,10 C 10,40 10,60 50,90" fill="none" stroke="url(#arcGrad1)" strokeWidth="0.5" filter="url(#glow)" className="arc-animate-3" data-id="element-126" />
          <path d="M 50,10 C 90,40 90,60 50,90" fill="none" stroke="url(#arcGrad2)" strokeWidth="0.5" filter="url(#glow)" className="arc-animate-4" data-id="element-127" />
          <path d="M 20,20 C 60,40 40,60 80,80" fill="none" stroke="url(#arcGrad1)" strokeWidth="0.5" filter="url(#glow)" className="arc-animate-5" data-id="element-128" />
        </svg>
      </div>

      <style data-id="element-129">{`
        .arc-animate-1 { stroke-dasharray: 100; animation: dash 3s linear infinite; }
        .arc-animate-2 { stroke-dasharray: 120; animation: dash 4s linear infinite reverse; }
        .arc-animate-3 { stroke-dasharray: 90; animation: dash 2.5s linear infinite; }
        .arc-animate-4 { stroke-dasharray: 110; animation: dash 3.5s linear infinite reverse; }
        .arc-animate-5 { stroke-dasharray: 130; animation: dash 5s linear infinite; }
        @keyframes dash { from { stroke-dashoffset: 200; } to { stroke-dashoffset: 0; } }
      `}</style>
    </motion.div>;
}